import { gql } from '@apollo/client'
import {
  AdminClosedDealsBillingStatus,
  IPaginationInput,
  IQuery
} from 'src/types/graphql'

/**
 * The admin panel's tab badge counts — ONE operation shared by the panel's
 * tab strip (`admin/index.tsx`) and the "Admin" badge everywhere else
 * (`useNotificationCounts`: sidebar, profile-button dot, mobile drawer). Same
 * document + same variables = one Apollo cache entry, so the panel's poll
 * refreshes the nav badge too, and the two can never disagree about what
 * "admin work pending" means.
 *
 * The ownership queue is deliberately NOT here — see
 * `ADMIN_OWNERSHIP_QUEUE_COUNT` (expensive, never polled).
 */
export const ADMIN_TAB_COUNTS = gql`
  query AdminTabCounts(
    $pagination: PaginationInput!
    $closedDealsFilter: AdminClosedDealsFilter
    $closedDealsPagination: PaginationInput!
  ) {
    userVerificationRequests(pagination: $pagination) {
      total
    }
    locationVerificationRequests(pagination: $pagination) {
      total
    }
    reportedLocations(pagination: $pagination) {
      total
    }
    reportedUsers(pagination: $pagination) {
      total
    }
    pastDueInvoices(pagination: $pagination) {
      total
    }
    pendingPlatformFacturasCount
    adminDirectoryClaims(pagination: $pagination, status: SUBMITTED) {
      total
    }
    adminClosedDeals(
      filter: $closedDealsFilter
      pagination: $closedDealsPagination
    ) {
      total
    }
  }
`

export type IAdminTabCounts = Pick<
  IQuery,
  | 'userVerificationRequests'
  | 'locationVerificationRequests'
  | 'reportedLocations'
  | 'reportedUsers'
  | 'pastDueInvoices'
  | 'pendingPlatformFacturasCount'
  | 'adminClosedDeals'
  | 'adminDirectoryClaims'
>

export type IAdminTabCountsArgs = {
  pagination: IPaginationInput
  closedDealsFilter: { billingStatus: AdminClosedDealsBillingStatus }
  closedDealsPagination: IPaginationInput
}

export const ADMIN_TAB_COUNTS_VARIABLES: IAdminTabCountsArgs = {
  pagination: { from: 0, pageSize: 0 },
  // Surface "deals needing triage" (no billing yet) as the tab badge — most
  // actionable signal for admins on the Finanzas group.
  closedDealsFilter: { billingStatus: AdminClosedDealsBillingStatus.None },
  closedDealsPagination: { from: 0, pageSize: 0 }
}

/** Sum of every tab badge the query feeds — the nav "Admin" badge. */
export const sumAdminTabCounts = (data: IAdminTabCounts | undefined): number =>
  (data?.userVerificationRequests?.total ?? 0) +
  (data?.locationVerificationRequests?.total ?? 0) +
  (data?.reportedLocations?.total ?? 0) +
  (data?.reportedUsers?.total ?? 0) +
  (data?.pastDueInvoices?.total ?? 0) +
  (data?.pendingPlatformFacturasCount ?? 0) +
  (data?.adminDirectoryClaims?.total ?? 0) +
  (data?.adminClosedDeals?.total ?? 0)
